import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import TradeForm from '../components/TradeForm'

export default function Journal() {
  const { user } = useAuth()
  const [trades, setTrades] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingTrade, setEditingTrade] = useState(null)
  const [filter, setFilter] = useState({ market_type: '', direction: '', symbol: '' })

  const fetchTrades = useCallback(async () => {
    setLoading(true)
    let query = supabase.from('trades').select('*').eq('user_id', user.id).order('entry_date', { ascending: false })
    if (filter.market_type) query = query.eq('market_type', filter.market_type)
    if (filter.direction) query = query.eq('direction', filter.direction)
    if (filter.symbol) query = query.ilike('symbol', `%${filter.symbol}%`)
    const { data } = await query
    setTrades(data || [])
    setLoading(false)
  }, [user.id, filter])

  useEffect(() => { fetchTrades() }, [fetchTrades])

  const deleteTrade = async (id) => {
    if (!window.confirm('Delete this trade?')) return
    await supabase.from('trades').delete().eq('id', id)
    fetchTrades()
  }

  const pnlColor = (pnl) => pnl > 0 ? 'text-green-400' : pnl < 0 ? 'text-red-400' : 'text-gray-400'

  const exportCSV = () => {
    const headers = ['Symbol', 'Market', 'Exchange', 'Direction', 'Entry Price', 'Exit Price', 'Quantity', 'Fees', 'PnL', 'PnL %', 'Entry Date', 'Exit Date', 'Status', 'Strategy', 'Notes']
    const rows = trades.map(t => [
      t.symbol, t.market_type, t.exchange, t.direction,
      t.entry_price, t.exit_price ?? '', t.quantity, t.fees,
      t.pnl ?? '', t.pnl_percentage ?? '',
      t.entry_date ? new Date(t.entry_date).toLocaleString() : '',
      t.exit_date ? new Date(t.exit_date).toLocaleString() : '',
      t.status, t.strategy ?? '', t.notes ?? ''
    ])
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `trading-journal-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
  URL.revokeObjectURL(url)
}

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Trade Journal</h1>
          <p className="text-gray-400 text-sm mt-1">{trades.length} trades logged</p>
        </div>
      </div>
      <div className="flex gap-3">
        <button
          onClick={exportCSV}
          className="border border-gray-700 hover:border-gray-500 text-gray-400 hover:text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          ↓ Export CSV
        </button>
        <button
          onClick={() => { setEditingTrade(null); setShowForm(true) }}
          className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          + Log Trade
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-6">
        <select
          value={filter.market_type}
          onChange={e => setFilter(p => ({ ...p, market_type: e.target.value }))}
          className="bg-gray-800 border border-gray-700 text-gray-300 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
        >
          <option value="">All Markets</option>
          <option value="crypto">Crypto</option>
          <option value="forex">Forex</option>
        </select>
        <select
          value={filter.direction}
          onChange={e => setFilter(p => ({ ...p, direction: e.target.value }))}
          className="bg-gray-800 border border-gray-700 text-gray-300 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
        >
          <option value="">All Directions</option>
          <option value="long">Long</option>
          <option value="short">Short</option>
        </select>
        <input
          value={filter.symbol}
          onChange={e => setFilter(p => ({ ...p, symbol: e.target.value }))}
          placeholder="Search symbol..."
          className="bg-gray-800 border border-gray-700 text-gray-300 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
        />
      </div>

      {/* Table */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 text-gray-400 text-xs uppercase">
                <th className="text-left px-4 py-3">Symbol</th>
                <th className="text-left px-4 py-3">Market</th>
                <th className="text-left px-4 py-3">Exchange</th>
                <th className="text-left px-4 py-3">Direction</th>
                <th className="text-right px-4 py-3">Entry</th>
                <th className="text-right px-4 py-3">Exit</th>
                <th className="text-right px-4 py-3">Qty</th>
                <th className="text-right px-4 py-3">PnL</th>
                <th className="text-right px-4 py-3">PnL %</th>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={12} className="text-center text-gray-500 py-12">Loading...</td></tr>
              ) : trades.length === 0 ? (
                <tr><td colSpan={12} className="text-center text-gray-500 py-12">No trades yet. Log your first trade!</td></tr>
              ) : trades.map(trade => (
                <tr key={trade.id} className="border-b border-gray-800 hover:bg-gray-800 transition-colors">
                  <td className="px-4 py-3 text-white font-medium">{trade.symbol}</td>
                  <td className="px-4 py-3 text-gray-400 capitalize">{trade.market_type}</td>
                  <td className="px-4 py-3 text-gray-400">{trade.exchange}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${trade.direction === 'long' ? 'bg-green-900 text-green-400' : 'bg-red-900 text-red-400'}`}>
                      {trade.direction}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-gray-300">{trade.entry_price}</td>
                  <td className="px-4 py-3 text-right text-gray-300">{trade.exit_price ?? '—'}</td>
                  <td className="px-4 py-3 text-right text-gray-300">{trade.quantity}</td>
                  <td className={`px-4 py-3 text-right font-medium ${pnlColor(trade.pnl)}`}>
                    {trade.pnl != null ? (trade.pnl > 0 ? '+' : '') + trade.pnl.toFixed(2) : '—'}
                  </td>
                  <td className={`px-4 py-3 text-right ${pnlColor(trade.pnl_percentage)}`}>
                    {trade.pnl_percentage != null ? (trade.pnl_percentage > 0 ? '+' : '') + trade.pnl_percentage.toFixed(2) + '%' : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-400">{new Date(trade.entry_date).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs ${trade.status === 'open' ? 'bg-yellow-900 text-yellow-400' : 'bg-gray-700 text-gray-400'}`}>
                      {trade.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => { setEditingTrade(trade); setShowForm(true) }} className="text-gray-400 hover:text-blue-400 transition-colors">✏️</button>
                      <button onClick={() => deleteTrade(trade.id)} className="text-gray-400 hover:text-red-400 transition-colors">🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <TradeForm
          onClose={() => { setShowForm(false); setEditingTrade(null) }}
          onSaved={fetchTrades}
          existingTrade={editingTrade}
        />
      )}
    </div>
  )
}